import React, { useState, useRef, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import {
  initGoogleAPI, isReady, signIn, signOut, getAccessToken,
  createSpreadsheet, syncToGoogleSheets, restoreFromGoogleSheets
} from "../../utils/gsheets";
import { Download, Upload, Cloud, CloudOff, Table, Shield, CheckCircle, AlertTriangle, RefreshCw, ExternalLink, Trash2, Database, HardDrive, FileText } from "lucide-react";

const LS_KEY = "fb11_gsheet_config";

function loadConfig() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
}
function saveConfig(c) { localStorage.setItem(LS_KEY, JSON.stringify(c)); }

export default function Backup() {
  const { expenses, team, cryptoHoldings, loanRequests, payments, auditLog, restoreBackup } = useApp();
  const [cfg, setCfg] = useState(loadConfig);
  const [clientId, setClientId] = useState(cfg.clientId || "");
  const [sheetId, setSheetId] = useState(cfg.sheetId || "");
  const [sheetUrl, setSheetUrl] = useState(cfg.sheetUrl || "");
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [ready, setReady] = useState(false);
  const [connected, setConnected] = useState(false);
  const [step, setStep] = useState(cfg.clientId ? 2 : 1);
  const fileRef = useRef(null);

  const allData = { expenses, team, cryptoHoldings, loanRequests, payments, auditLog };

  useEffect(() => {
    if (cfg.clientId) {
      initGoogleAPI(cfg.clientId).then(() => { setReady(true); if (getAccessToken()) setConnected(true); }).catch(e => console.error(e));
    }
  }, [cfg.clientId]);

  const handleSetup = async () => {
    if (!clientId.trim()) { setErr("Client ID required"); return; }
    try {
      setStatus("Initializing Google API...");
      await initGoogleAPI(clientId.trim());
      setReady(true);
      const newCfg = { ...cfg, clientId: clientId.trim() };
      setCfg(newCfg);
      saveConfig(newCfg);
      setStep(2);
      setStatus("Google API ready! Now sign in.");
      setTimeout(() => setStatus(""), 3000);
    } catch (e) { setErr("Failed to init: " + e.message); }
  };

  const handleSignIn = async () => {
    try {
      setStatus("Signing in...");
      await signIn();
      setConnected(true);
      setStatus("Connected to Google!");
      setTimeout(() => setStatus(""), 3000);
    } catch (e) { setErr("Sign-in failed: " + (e.message || JSON.stringify(e))); }
  };

  const handleSignOut = () => {
    signOut();
    setConnected(false);
    setStatus("Disconnected");
    setTimeout(() => setStatus(""), 2000);
  };

  const handleSync = async () => {
    setSyncing(true);
    setErr("");
    try {
      let sid = sheetId;
      if (!sid) {
        setStatus("Creating new spreadsheet...");
        const sheet = await createSpreadsheet("FinBoard Backup - " + new Date().toLocaleDateString("en-IN"));
        sid = sheet.spreadsheetId;
        const url = "https://docs.google.com/spreadsheets/d/" + sid;
        setSheetId(sid);
        setSheetUrl(url);
        const newCfg = { ...cfg, sheetId: sid, sheetUrl: url };
        setCfg(newCfg);
        saveConfig(newCfg);
      }
      setStatus("Syncing data to Google Sheets...");
      const ts = await syncToGoogleSheets(sid, allData);
      const newCfg = { ...cfg, sheetId: sid, lastSync: ts };
      setCfg(newCfg);
      saveConfig(newCfg);
      setStatus("Synced successfully at " + ts);
    } catch (e) {
      setErr("Sync failed: " + (e.message || e.result?.error?.message || "Unknown error"));
    }
    setSyncing(false);
  };

  const handleRestore = async () => {
    if (!sheetId) { setErr("No spreadsheet linked"); return; }
    setSyncing(true);
    try {
      setStatus("Reading data from Google Sheets...");
      const data = await restoreFromGoogleSheets(sheetId);
      if (restoreBackup) {
        restoreBackup(data);
        setStatus("Data restored from Google Sheets!");
      }
    } catch (e) { setErr("Restore failed: " + (e.message || "Unknown error")); }
    setSyncing(false);
  };

  const handleLocalBackup = () => {
    const backup = { _meta: { app: "FinBoard v11.3", exportedAt: new Date().toISOString() }, ...allData };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "FinBoard_Backup_" + new Date().toISOString().split("T")[0] + ".json"; a.click();
    setStatus("Local backup downloaded!");
    setTimeout(() => setStatus(""), 3000);
  };

  const handleLocalRestore = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data._meta || data._meta.app !== "FinBoard v11.3") throw new Error("Invalid backup");
      if (restoreBackup) { restoreBackup(data); setStatus("Restored from local backup!"); }
    } catch (err) { setErr("Invalid file: " + err.message); }
    e.target.value = "";
  };

  const handleReset = () => {
    localStorage.removeItem(LS_KEY);
    setCfg({});
    setClientId("");
    setSheetId("");
    setSheetUrl("");
    setStep(1);
    setConnected(false);
    setStatus("Config reset");
  };

  const stats = [
    { label:"Expenses", count:expenses.length, color:"#EF4444" },
    { label:"Team", count:team.length, color:"#3B82F6" },
    { label:"Crypto", count:cryptoHoldings.length, color:"#F5A623" },
    { label:"Loans", count:loanRequests.length, color:"#8B5CF6" },
    { label:"Audit", count:auditLog.length, color:"#10B981" },
  ];

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h3>Backup & Restore</h3><span className="text-muted">{connected ? "Connected to Google" : "Not connected"}</span></div>
        <div style={{display:"flex",gap:8}}>
          <button className="btn-ghost" onClick={handleLocalBackup} style={{display:"flex",alignItems:"center",gap:6}}><Download size={14}/> Local Backup</button>
        </div>
      </div>

      {status && <div style={{padding:"12px 16px",borderRadius:10,background:"rgba(16,185,129,.1)",border:"1px solid rgba(16,185,129,.3)",color:"#10B981",fontSize:13,marginBottom:16,display:"flex",alignItems:"center",gap:8}}><CheckCircle size={16}/> {status}</div>}
      {err && <div style={{padding:"12px 16px",borderRadius:10,background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.3)",color:"#EF4444",fontSize:13,marginBottom:16,display:"flex",alignItems:"center",gap:8}}><AlertTriangle size={16}/> {err} <button onClick={()=>setErr("")} style={{marginLeft:"auto",background:"none",border:"none",color:"#EF4444",cursor:"pointer"}}>x</button></div>}

      {/* DATA COUNT */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10,marginBottom:24}}>
        {stats.map(s => <div key={s.label} className="card" style={{padding:14,textAlign:"center"}}><div style={{fontSize:22,fontWeight:800,color:s.color,fontFamily:"monospace"}}>{s.count}</div><div style={{fontSize:10,color:"#64748B"}}>{s.label}</div></div>)}
      </div>

      {/* GOOGLE SHEETS SETUP */}
      <div className="card" style={{padding:24,marginBottom:20}}>
        <h4 style={{fontSize:16,fontWeight:700,color:"#E2E8F0",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
          <Cloud size={20} style={{color:"#4285F4"}}/> Google Sheets Sync
        </h4>

        {/* STEP 1: Client ID */}
        <div style={{marginBottom:20,padding:16,borderRadius:10,background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.06)"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#F5A623",marginBottom:8}}>Step 1: Google Cloud Setup</div>
          <div style={{fontSize:12,color:"#94A3B8",lineHeight:1.7,marginBottom:12}}>
            1. Go to <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" style={{color:"#4285F4"}}>console.cloud.google.com</a><br/>
            2. Create new project or select existing<br/>
            3. Enable <strong style={{color:"#CBD5E1"}}>Google Sheets API</strong> + <strong style={{color:"#CBD5E1"}}>Google Drive API</strong><br/>
            4. Go to <strong style={{color:"#CBD5E1"}}>Credentials &gt; Create OAuth 2.0 Client ID</strong><br/>
            5. Application type: <strong style={{color:"#CBD5E1"}}>Web application</strong><br/>
            6. Add Authorized JS origin: <strong style={{color:"#10B981"}}>{window.location.origin}</strong><br/>
            7. Copy the <strong style={{color:"#CBD5E1"}}>Client ID</strong> and paste below
          </div>
          <div style={{display:"flex",gap:8}}>
            <input type="text" value={clientId} onChange={e => setClientId(e.target.value)} placeholder="Paste OAuth Client ID here..." style={{flex:1,background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.1)",borderRadius:8,padding:"10px 14px",color:"#E2E8F0",fontSize:13,outline:"none",fontFamily:"monospace"}}/>
            <button onClick={handleSetup} className="btn-gold" style={{whiteSpace:"nowrap"}}>{ready ? "Updated" : "Setup"}</button>
          </div>
        </div>

        {/* STEP 2: Sign In + Sync */}
        {ready && (
          <div style={{padding:16,borderRadius:10,background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.06)"}}>
            <div style={{fontSize:12,fontWeight:700,color:"#F5A623",marginBottom:12}}>Step 2: Connect & Sync</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16}}>
              {!connected ? (
                <button onClick={handleSignIn} className="btn-gold" style={{display:"flex",alignItems:"center",gap:8,background:"#4285F4"}}><Cloud size={16}/> Sign in with Google</button>
              ) : (
                <>
                  <button onClick={handleSync} disabled={syncing} className="btn-gold" style={{display:"flex",alignItems:"center",gap:8}}>
                    <RefreshCw size={14} className={syncing?"spin":""}/> {syncing ? "Syncing..." : sheetId ? "Sync Now" : "Create Sheet & Sync"}
                  </button>
                  <button onClick={handleRestore} disabled={syncing || !sheetId} className="btn-ghost" style={{display:"flex",alignItems:"center",gap:8}}>
                    <Upload size={14}/> Restore from Sheet
                  </button>
                  <button onClick={handleSignOut} className="btn-ghost" style={{display:"flex",alignItems:"center",gap:8,color:"#EF4444"}}>
                    <CloudOff size={14}/> Disconnect
                  </button>
                </>
              )}
            </div>

            {sheetUrl && (
              <div style={{padding:12,borderRadius:8,background:"rgba(66,133,244,.08)",border:"1px solid rgba(66,133,244,.2)",display:"flex",alignItems:"center",gap:10}}>
                <Table size={16} style={{color:"#4285F4"}}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,color:"#94A3B8"}}>Linked Spreadsheet</div>
                  <a href={sheetUrl} target="_blank" rel="noreferrer" style={{fontSize:13,color:"#4285F4",wordBreak:"break-all"}}>{sheetUrl}</a>
                </div>
                <a href={sheetUrl} target="_blank" rel="noreferrer" style={{color:"#4285F4"}}><ExternalLink size={16}/></a>
              </div>
            )}
            {cfg.lastSync && <div style={{fontSize:11,color:"#64748B",marginTop:10}}>Last synced: {cfg.lastSync}</div>}
          </div>
        )}
      </div>

      {/* LOCAL BACKUP / RESTORE */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
        <div className="card" style={{padding:24}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
            <div style={{width:40,height:40,borderRadius:10,background:"rgba(16,185,129,.12)",color:"#10B981",display:"flex",alignItems:"center",justifyContent:"center"}}><Download size={20}/></div>
            <div><div style={{fontWeight:700,color:"#E2E8F0",fontSize:14}}>Local Backup</div><div style={{fontSize:10,color:"#64748B"}}>Download JSON file</div></div>
          </div>
          <p style={{fontSize:12,color:"#94A3B8",lineHeight:1.6,marginBottom:14}}>Full data backup as JSON. Can be restored anytime.</p>
          <button className="btn-gold full" onClick={handleLocalBackup}><Download size={14}/> Download Backup</button>
        </div>

        <div className="card" style={{padding:24}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
            <div style={{width:40,height:40,borderRadius:10,background:"rgba(245,166,35,.12)",color:"#F5A623",display:"flex",alignItems:"center",justifyContent:"center"}}><Upload size={20}/></div>
            <div><div style={{fontWeight:700,color:"#E2E8F0",fontSize:14}}>Restore Backup</div><div style={{fontSize:10,color:"#64748B"}}>Upload JSON file</div></div>
          </div>
          <p style={{fontSize:12,color:"#94A3B8",lineHeight:1.6,marginBottom:14}}>Upload a FinBoard backup to restore all data.</p>
          <input type="file" accept=".json" ref={fileRef} onChange={handleLocalRestore} style={{display:"none"}}/>
          <button className="btn-gold full" onClick={() => fileRef.current.click()} style={{background:"#F97316"}}><Upload size={14}/> Upload & Restore</button>
        </div>
      </div>

      {/* Reset */}
      <div style={{textAlign:"center"}}><button onClick={handleReset} style={{background:"none",border:"none",color:"#475569",cursor:"pointer",fontSize:11}}>Reset Google config</button></div>
    </div>
  );
}
